import { z } from "zod";

// Requisitos de senha (devem bater com o backend)
const passwordRequirements = {
  minLength: 8,
  hasUppercase: /[A-Z]/,
  hasLowercase: /[a-z]/,
  hasNumber: /[0-9]/,
  hasSpecial: /[!@#$%^&*(),.?":{}|<>]/,
};

// Schema para login
export const loginSchema = z.object({
  email: z
    .string()
    .min(1, "Email é obrigatório")
    .email("Email inválido"),
  password: z
    .string()
    .min(1, "Senha é obrigatória"),
});

export type LoginInput = z.infer<typeof loginSchema>;

// Schema para nova senha (com todas as validações de complexidade)
export const newPasswordSchema = z
  .string()
  .min(passwordRequirements.minLength, `Senha deve ter no mínimo ${passwordRequirements.minLength} caracteres`)
  .regex(passwordRequirements.hasUppercase, "Senha deve conter pelo menos uma letra maiúscula")
  .regex(passwordRequirements.hasLowercase, "Senha deve conter pelo menos uma letra minúscula")
  .regex(passwordRequirements.hasNumber, "Senha deve conter pelo menos um número")
  .regex(passwordRequirements.hasSpecial, "Senha deve conter pelo menos um caractere especial (!@#$%^&*...)");

// Schema para troca de senha
export const changePasswordSchema = z.object({
  currentPassword: z
    .string()
    .min(1, "Senha atual é obrigatória"),
  newPassword: newPasswordSchema,
  confirmPassword: z
    .string()
    .min(1, "Confirmação de senha é obrigatória"),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "As senhas não coincidem",
  path: ["confirmPassword"],
}).refine((data) => data.currentPassword !== data.newPassword, {
  message: "A nova senha deve ser diferente da senha atual",
  path: ["newPassword"],
});

export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;

// Interface para estado de validação de requisitos de senha
export interface PasswordRequirementState {
  minLength: boolean;
  hasUppercase: boolean;
  hasLowercase: boolean;
  hasNumber: boolean;
  hasSpecial: boolean;
}

// Função helper para verificar requisitos de senha individualmente
export function checkPasswordRequirements(password: string): PasswordRequirementState {
  return {
    minLength: password.length >= passwordRequirements.minLength,
    hasUppercase: passwordRequirements.hasUppercase.test(password),
    hasLowercase: passwordRequirements.hasLowercase.test(password),
    hasNumber: passwordRequirements.hasNumber.test(password),
    hasSpecial: passwordRequirements.hasSpecial.test(password),
  };
}

// Função helper para verificar se todos os requisitos foram atendidos
export function isPasswordStrong(password: string): boolean {
  const requirements = checkPasswordRequirements(password);
  return Object.values(requirements).every(Boolean);
}

// Constantes exportadas para uso na UI
export const PASSWORD_REQUIREMENTS = [
  { key: "minLength", label: `Mínimo ${passwordRequirements.minLength} caracteres` },
  { key: "hasUppercase", label: "Uma letra maiúscula" },
  { key: "hasLowercase", label: "Uma letra minúscula" },
  { key: "hasNumber", label: "Um número" },
  { key: "hasSpecial", label: "Um caractere especial (!@#$%^&*...)" },
] as const;

// Schema para resposta de autenticação
export const authResponseSchema = z.object({
  accessToken: z.string(),
  user: z.object({
    id: z.string().uuid(),
    email: z.string().email(),
    name: z.string(),
    role: z.string(),
    type: z.enum(["internal", "company"]),
    companyId: z.string().uuid().optional().nullable(),
    companyName: z.string().optional().nullable(),
    mustChangePassword: z.boolean().optional(),
  }),
});

export type AuthResponse = z.infer<typeof authResponseSchema>;
